<template>
  <div class="flex flex-wrap items-start gap-8">
    <div class="flex min-w-0 flex-[1_1_22.5rem] flex-col gap-8">
      <WebsiteAutofill />
      <section class="flex flex-col gap-3">
        <div class="flex flex-col gap-1">
          <h2 class="text-base font-semibold">{{ t('admin.setup.branding.logos.title') }}</h2>
        </div>
        <LogoUpload id="setup-logo" v-model="form.LOGO_SMALL" />
      </section>

      <section class="flex flex-col gap-3">
        <h2 class="text-base font-semibold">{{ t('admin.setup.branding.colors.title') }}</h2>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div v-for="color in COLOR_FIELDS" :key="color.name" class="flex flex-col gap-1.5">
            <Label :for="`setup-${color.name}`">{{ t(color.labelKey) }}</Label>
            <div class="flex items-center gap-2">
              <input
                type="color"
                :value="pickerValue(form[color.name], color.fallback)"
                class="size-9 shrink-0 cursor-pointer rounded-sm border border-input bg-transparent p-0.5"
                aria-hidden="true"
                tabindex="-1"
                @input="form[color.name] = ($event.target as HTMLInputElement).value"
              />
              <Input
                :id="`setup-${color.name}`"
                v-model="form[color.name]"
                class="font-mono"
                placeholder="#3936bd"
                :data-test-id="`setup-${color.name}-input`"
              />
            </div>
          </div>
        </div>
        <p
          v-if="primaryRatioRef && !primaryNeedsFixRef"
          role="status"
          class="flex items-start gap-2 rounded-md bg-muted p-3 text-sm leading-normal"
        >
          <CircleCheckIcon class="mt-0.5 size-4 shrink-0 text-success" aria-hidden="true" />
          {{ t('admin.setup.branding.contrastOk', { ratio: primaryRatioRef }) }}
        </p>
        <ColorContrastProposal :hex="form.COLOR_PRIMARY" @accept="(color) => (form.COLOR_PRIMARY = color)" />
      </section>

      <!-- CSS brut injecté dans toutes les pages : proposé dans « Ma plateforme », pas dans l'assistant. -->
      <section v-if="withCustomStyle" class="flex flex-col gap-3" data-test-id="setup-custom-style">
        <div class="flex flex-col gap-1">
          <div class="flex items-center justify-between gap-3">
            <h2 class="text-base font-semibold">{{ t('admin.setup.branding.customStyle.title') }}</h2>
            <Button
              variant="link"
              size="sm"
              class="h-auto p-0"
              :aria-expanded="isCustomStyleOpenRef"
              aria-controls="setup-custom-style-panel"
              data-test-id="setup-custom-style-toggle"
              @click="isCustomStyleOpenRef = !isCustomStyleOpenRef"
            >
              {{
                isCustomStyleOpenRef
                  ? t('admin.setup.branding.customStyle.hide')
                  : t('admin.setup.branding.customStyle.show')
              }}
            </Button>
          </div>
          <p class="text-sm text-muted-foreground">
            {{
              form.CUSTOM_STYLE
                ? t('admin.setup.branding.customStyle.active')
                : t('admin.setup.branding.customStyle.description')
            }}
          </p>
        </div>
        <div v-if="isCustomStyleOpenRef" id="setup-custom-style-panel" class="flex flex-col gap-2">
          <p class="rounded-md bg-muted p-3 text-xs leading-relaxed text-muted-foreground">
            {{ t('admin.setup.branding.customStyle.warning') }}
          </p>
          <Label for="setup-custom-style">{{ t('admin.setup.branding.customStyle.label') }}</Label>
          <Textarea
            id="setup-custom-style"
            v-model="form.CUSTOM_STYLE"
            rows="8"
            spellcheck="false"
            class="font-mono text-xs"
            placeholder=".exemple { color: #0f6e5c; }"
            data-test-id="setup-custom-style-input"
          />
        </div>
      </section>
    </div>

    <figure class="m-0 flex min-w-0 flex-[1_1_20rem] flex-col gap-2" data-test-id="setup-branding-preview">
      <figcaption class="text-xs text-muted-foreground">{{ t('admin.setup.branding.preview.caption') }}</figcaption>
      <div class="overflow-hidden rounded-lg border shadow-md" aria-hidden="true">
        <div class="flex h-7 items-center gap-1.5 border-b bg-muted px-2.5">
          <img v-if="form.LOGO_SMALL" :src="form.LOGO_SMALL" alt="" class="size-3! shrink-0 object-contain!" />
          <span v-else class="size-3 rounded-[3px] bg-border" />
          <span class="truncate text-xs text-muted-foreground">{{ form.TITLE || form.COMPANY_NAME }}</span>
        </div>
        <div class="flex min-h-75 items-center justify-center px-4 py-7" :style="{ background: secondaryRef }">
          <div class="flex w-full max-w-65 flex-col gap-3 rounded-lg bg-white p-5 text-[#1b1b1e]">
            <div class="flex items-center gap-2">
              <img
                v-if="form.LOGO_SMALL"
                :src="form.LOGO_SMALL"
                alt=""
                class="size-7! shrink-0 rounded-sm object-contain!"
              />
              <span class="truncate text-xs font-bold">{{ form.TITLE || form.COMPANY_NAME }}</span>
            </div>
            <span class="text-base font-semibold">{{ t('admin.setup.branding.preview.login') }}</span>
            <span class="flex h-8 items-center rounded-sm border border-[#e5e5ec] px-2 text-xs text-[#61616e]">
              {{ t('admin.setup.branding.preview.email') }}
            </span>
            <span
              class="flex h-8 items-center justify-center rounded-sm text-xs font-semibold text-white"
              :style="{ background: primaryRef }"
            >
              {{ t('admin.setup.branding.preview.continue') }}
            </span>
            <span class="text-center text-xs text-[#61616e]">
              {{ t('admin.setup.branding.preview.help') }}
              <span class="font-semibold" :style="{ color: primaryRef }">{{
                t('admin.setup.branding.preview.support')
              }}</span>
            </span>
          </div>
        </div>
      </div>
    </figure>
  </div>
</template>

<script setup lang="ts">
import { CircleCheckIcon } from 'lucide-vue-next'
import { computed, ref } from 'vue'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import LogoUpload from '@/features/admin/platform-setup/components/logo-upload.vue'
import WebsiteAutofill from '@/features/admin/platform-setup/components/steps/website-autofill.vue'
import type { PlatformSettingNameType } from '@/features/admin/platform-setup/constants'
import { usePlatformSetup } from '@/features/admin/platform-setup/hooks/use-platform-setup.hook'
import ColorContrastProposal from '@/features/admin/settings/components/color-contrast-proposal.vue'
import { useI18n } from '@/lib/i18n'
import { meetsTextContrast, parseHexColor, textContrastRatio } from '@/utils/misc/contrast'

withDefaults(defineProps<{ withCustomStyle?: boolean }>(), { withCustomStyle: false })

const isCustomStyleOpenRef = ref(false)

const DEFAULT_PRIMARY = '#3936bd'
const DEFAULT_SECONDARY = '#f3f3f7'

const COLOR_FIELDS: { name: PlatformSettingNameType; labelKey: string; fallback: string }[] = [
  { name: 'COLOR_PRIMARY', labelKey: 'admin.setup.branding.primary.label', fallback: DEFAULT_PRIMARY },
  { name: 'COLOR_SECONDARY', labelKey: 'admin.setup.branding.secondary.label', fallback: DEFAULT_SECONDARY },
]

const { t, locale } = useI18n()
const { form } = usePlatformSetup()

const validHex = (value: string) => (parseHexColor(value.trim()) ? value.trim() : null)
const primaryRef = computed(() => validHex(form.COLOR_PRIMARY) ?? DEFAULT_PRIMARY)
const secondaryRef = computed(() => validHex(form.COLOR_SECONDARY) ?? DEFAULT_SECONDARY)

// <input type="color"> n'accepte que #rrggbb.
function pickerValue(value: string, fallback: string) {
  const hex = validHex(value)
  if (!hex) return fallback
  if (hex.length === 4) return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`.toLowerCase()
  return hex.slice(0, 7).toLowerCase()
}

const primaryNeedsFixRef = computed(
  () => !!validHex(form.COLOR_PRIMARY) && !meetsTextContrast(form.COLOR_PRIMARY.trim()),
)
const primaryRatioRef = computed(() => {
  const hex = validHex(form.COLOR_PRIMARY)
  const ratio = hex ? textContrastRatio(hex) : null
  if (ratio === null) return ''
  return new Intl.NumberFormat(locale.value, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(ratio)
})
</script>
